import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "@mantine/core";
import { IconFilter } from "@tabler/icons-react";
import { fetchMattersList } from "../../../actions/matterActions";
import { contextItemIcon } from "../contextItemIcon";
import {
	contextItemKey,
	filterMatches,
	truncateClientName,
} from "../contextItemUtils";
import ClearableTextInput from "../ClearableTextInput";
import RowSubtitle from "../RowSubtitle";
import BrowseRow from "./BrowseRow";
import BrowseSortMenu from "./BrowseSortMenu";
import useProgressiveReveal from "./useProgressiveReveal";

const SORT_OPTIONS = [
	{ key: "nameAsc", label: "Name (A-Z)" },
	{ key: "nameDesc", label: "Name (Z-A)" },
	{ key: "clientAsc", label: "Client Name (A-Z)" },
	{ key: "dateOpenedDesc", label: "Date Opened (Newest)" },
	{ key: "dateOpenedAsc", label: "Date Opened (Oldest)" },
];

const sortMatters = (matters, sort) => {
	const sorted = [...matters];
	switch (sort) {
		case "nameDesc":
			sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
			break;
		case "clientAsc":
			sorted.sort((a, b) =>
				(a.clientName || "").localeCompare(b.clientName || "")
			);
			break;
		case "dateOpenedDesc":
			sorted.sort(
				(a, b) =>
					new Date(b.dateOpened || 0) - new Date(a.dateOpened || 0)
			);
			break;
		case "dateOpenedAsc":
			sorted.sort(
				(a, b) =>
					new Date(a.dateOpened || 0) - new Date(b.dateOpened || 0)
			);
			break;
		case "nameAsc":
		default:
			sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
	}
	return sorted;
};

// Top tier of Browse (Full Scope AI only): the firm's matters, sourced from
// the same Redux list/action the main Matters page already uses
// (state.matter.mattersList via fetchMattersList), with Public/Private Firm
// Docs mixed in via includeFirmDocs — the backend already includes those
// only when the current user is permitted to see them.
const MattersBrowseList = ({
	existingItems,
	selected,
	onToggle,
	onPreview,
	onSelectMatter,
	browseState,
	onUpdateBrowseState,
}) => {
	const dispatch = useDispatch();
	const mattersList = useSelector((state) => state.matter.mattersList) || [];
	const [loading, setLoading] = useState(mattersList.length === 0);
	// lifted to AiAddContextModal so it survives the modal closing/reopening
	const { filterText, mattersSort: sort } = browseState;

	useEffect(() => {
		/*
			Always refetch on mount. mattersList is shared Redux state that the
			Matters page fills without includeFirmDocs, and ClientDetail fills
			scoped to a single client — reusing either silently drops the Firm
			Docs rows or shows one client's matters as if they were all of them.
			Anything already cached still renders immediately (see the `loading`
			initial state), so this refreshes underneath rather than blanking.

			quiet: true — this modal has its own inline spinner below, and
			doesn't want the app's full-page loading blocker layering on top.
		*/
		dispatch(fetchMattersList({ includeFirmDocs: true }, { quiet: true }));
	}, [dispatch]);

	useEffect(() => {
		if (mattersList.length > 0) setLoading(false);
	}, [mattersList.length]);

	const handleRefresh = () =>
		dispatch(fetchMattersList({ includeFirmDocs: true }, { quiet: true }));

	const { firmDocsRows, matterRows } = useMemo(() => {
		const firmDocs = [];
		const matters = [];
		mattersList.forEach((m) => {
			if (m.isDeleted) return;
			if (m.id === -1 || m.id === 0) {
				firmDocs.push(m);
			} else {
				matters.push(m);
			}
		});
		return { firmDocsRows: firmDocs, matterRows: matters };
	}, [mattersList]);

	const allRows = useMemo(() => {
		const text = filterText.trim().toLowerCase();
		const filteredFirmDocs = text
			? firmDocsRows.filter((m) => filterMatches(text, m.name))
			: firmDocsRows;
		const filteredMatters = text
			? matterRows.filter((m) =>
					filterMatches(
						text,
						m.name,
						m.clientName,
						m.customMatterID,
						m.id
					)
				)
			: matterRows;
		// Firm Docs stay pinned first (matching the order GET /matters
		// already returns them in) rather than being sorted in alphabetically
		return [...filteredFirmDocs, ...sortMatters(filteredMatters, sort)];
	}, [firmDocsRows, matterRows, filterText, sort]);

	const { visibleItems, containerRef, onScroll } =
		useProgressiveReveal(allRows);

	const existingKeys = new Set(
		(existingItems || []).map((item) => contextItemKey(item))
	);

	return (
		<div
			style={{ display: "flex", flexDirection: "column", height: "100%" }}
		>
			<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
				<ClearableTextInput
					style={{ flex: 1 }}
					placeholder="Filter by matter name, ID, or client name..."
					leftSection={<IconFilter size={16} />}
					value={filterText}
					onChange={(e) =>
						onUpdateBrowseState({
							filterText: e.currentTarget.value,
						})
					}
					onClear={() => onUpdateBrowseState({ filterText: "" })}
				/>
				<BrowseSortMenu
					sortOptions={SORT_OPTIONS}
					activeSort={sort}
					onChange={(key) =>
						onUpdateBrowseState({ mattersSort: key })
					}
					onRefresh={handleRefresh}
				/>
			</div>
			<div
				ref={containerRef}
				onScroll={onScroll}
				style={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					marginTop: 12,
				}}
			>
				{loading ? (
					<div style={{ textAlign: "center", padding: 24 }}>
						<Loader size={24} />
					</div>
				) : allRows.length === 0 ? (
					<div
						style={{
							padding: 24,
							textAlign: "center",
							color: "#888",
						}}
					>
						No matters found.
					</div>
				) : (
					visibleItems.map((matter) => {
						const isFirmDocs = matter.id === -1 || matter.id === 0;
						const item = {
							itemType: "Matter",
							id: matter.id,
							title: matter.name,
							clientName: matter.clientName,
							customMatterID: matter.customMatterID,
						};
						const key = contextItemKey(item);
						return (
							<BrowseRow
								key={key}
								icon={contextItemIcon(
									"Matter",
									matter.name,
									isFirmDocs
								)}
								title={matter.name}
								subtitle={
									isFirmDocs ? null : (
										<RowSubtitle
											text={truncateClientName(
												matter.clientName
											)}
											item={item}
											highlightQuery={filterText}
										/>
									)
								}
								highlightQuery={filterText}
								checked={
									!!selected[key] || existingKeys.has(key)
								}
								disabled={existingKeys.has(key)}
								onToggle={() => onToggle(item)}
								onClick={() => onSelectMatter(matter)}
								onPreview={
									// Firm Docs isn't a real matter, so there's
									// nothing to preview — same rule
									// CitationsSidebar already follows.
									isFirmDocs
										? undefined
										: () =>
												onPreview({
													itemType: "Matter",
													id: matter.id,
													title: matter.name,
												})
								}
							/>
						);
					})
				)}
			</div>
		</div>
	);
};

export default MattersBrowseList;
